import { Observable } from 'rxjs';
import { first, map, switchMap, withLatestFrom } from 'rxjs/operators';

import { ofType } from '@mode/legacy/util-domain';
import { Actions } from '~/actions';
import { AppStore } from '~/types/main.types';

import { ChartActions, fromChartSelectors } from '@mode/charts/domain';
import { DeprecatedApiService, TableResource } from '@mode/legacy/data-access-model-js';
import { Resource } from '@mode/shared/util-halogen';
import { TableState } from '@mode/legacy/types-app-state';
import { ChartTypes } from '@mode/shared/contract-common';
import { ToastService } from '@mode/shared/util-toast';
import { ChartModelActionType } from '../actions/chart.actions';
import { QueryActionType } from '../actions/query.actions';
import { SelectedReportSelectors } from '../selectors/selected-report.selectors';
import DatasetService from './dataset-service';
import { ReportDataService } from './report-data-service';
import { ReportSaveService } from './report-save-service';
import ViewService from './view-service';

class ChartDataService {
  public static $inject = [
    '$q',
    'DatasetService',
    'RxStoreService',
    'actions$',
    'ViewService',
    'ApiService',
    'ReportSaveService',
    '$rootScope',
    'ReportDataService',
    'ToastService',
  ];

  constructor(
    private $q: angular.IQService,
    private ds: DatasetService,
    private store: AppStore,
    private actions$: Observable<Actions>,
    private viewService: ViewService,
    private api: DeprecatedApiService,
    private reportSaveService: ReportSaveService,
    private $rootScope: angular.IRootScopeService,
    private reportDataService: ReportDataService,
    private alertService: ToastService
  ) {
    /**
     * This effect converts the viz resource to viz resource models.
     *
     * It is a temporary helper until much of the legacy resource/api code is migrated.
     */
    this.actions$
      .pipe(
        ofType(ChartActions.convertLoadedCharts.type),
        map(({ payload, queryToken }) => {
          const processedViz = payload.map((unprocessedViz: ChartTypes.ChartResource) => {
            return new Resource(unprocessedViz) as any as ChartTypes.ChartResource;
          });

          return { processedViz, queryToken };
        })
      )
      .subscribe(({ processedViz, queryToken }) => {
        this.store.dispatch(
          ChartActions.loadChartsSuccess({
            queryToken,
            payload: processedViz,
          })
        );
      });

    this.actions$
      .pipe(
        ofType(QueryActionType.LoadQueriesSuccess),
        switchMap((action) => {
          const promises = [];
          for (const query of action.queries) {
            if (query.hasEmbedded('charts')) {
              // We only want to dispatch legacy visualizations via this flow.
              // We are not loading legacy visualizations via the new Angular based data-access flow.
              // Note - This will now be responsible for legacy viz in the viewer case as well since this
              // is a shared effect. Legacy viz will now be filtered out of the new data-access flow.
              promises.push(
                this.api
                  .getResources<ChartTypes.ChartResource>(query, 'charts')
                  .then((charts) => {
                    const legacyCharts = charts.filter((chart) => chart.view_version === 1);
                    if (legacyCharts) {
                      this.store.dispatch(
                        ChartActions.loadChartsSuccess({
                          queryToken: query.token,
                          payload: legacyCharts,
                        })
                      );
                    }
                  })
                  .catch((err) => this.store.dispatch(ChartActions.loadChartsFailure({ error: err })))
              );
            }

            // We are still dispatch legacy query tables here
            if (query.hasEmbedded('query_tables')) {
              promises.push(
                this.api
                  .getResources<TableResource>(query, 'query_tables')
                  .then((tables) =>
                    this.store.dispatch({
                      type: ChartModelActionType.LoadEmbeddedTablesSuccess,
                      queryToken: query.token,
                      payload: tables,
                    })
                  )
                  .catch((err) =>
                    this.store.dispatch({ type: ChartModelActionType.LoadEmbeddedTablesFailure, error: err })
                  )
              );
            }
          }

          return this.$q.all(promises);
        })
      )
      .subscribe();

    // Table Effects
    this.actions$
      .pipe(
        ofType(ChartModelActionType.UpdateTableColumnSettings),
        withLatestFrom(this.store.select(SelectedReportSelectors.getTables), (action, tables) => ({
          action,
          table: tables.getRecord(action.token),
        })),
        switchMap(({ table }) =>
          this.saveTableSettings(table)
            .then((res) => this.store.dispatch({ type: ChartModelActionType.UpdateTableSuccess, tableResource: res }))
            .catch((err) =>
              this.store.dispatch({
                type: ChartModelActionType.UpdateTableColumnSettingsFailure,
                token: table.token,
                error: err,
              })
            )
        )
      )
      .subscribe();

    this.actions$
      .pipe(
        ofType(ChartModelActionType.UpdateTableExcludedColumns),
        withLatestFrom(this.store.select(SelectedReportSelectors.getTables), (action, tables) => ({
          action,
          table: tables.getRecord(action.token),
        })),
        switchMap(({ table }) =>
          this.saveTableSettings(table)
            .then((res) => this.store.dispatch({ type: ChartModelActionType.UpdateTableSuccess, tableResource: res }))
            .catch((err) =>
              this.store.dispatch({
                type: ChartModelActionType.UpdateTableExcludedColumnsFailure,
                token: table.token,
                error: err,
              })
            )
        )
      )
      .subscribe();

    // Destroy table effect
    this.actions$
      .pipe(
        ofType(ChartModelActionType.DestroyTable),
        switchMap(
          (action) =>
            this.store.select(SelectedReportSelectors.createTableResourceSelector(action.token)).pipe(first()),
          (action, table) => ({ action, table })
        )
      )
      .subscribe(({ action, table }) => {
        table
          .forms('destroy')
          .$submit()
          .then(() => {
            // remove table
            this.reportSaveService.removeTable(table.token);

            // clear selected if the table is selected
            if (this.viewService.views.viz.token === table.token) {
              this.viewService.clearSelected();
            }

            // Show the current query if the deleted chart is the currently selected one
            if (this.viewService.views.queryMode && this.viewService.views.viz.token === table.token) {
              this.$rootScope.$broadcast('report.selectQuery', {
                query: this.viewService.views.selectedQuery.token,
              });
            }
            this.store.dispatch({
              type: ChartModelActionType.DestroyTableSuccess,
              token: table.token,
              queryToken: action.queryToken,
            });

            this.reportDataService.updateReportSilently();
          })
          .catch(() => {
            alertService.error('There was a problem deleting your table. Please retry.');
          });
      });

    this.actions$
      .pipe(
        ofType(ChartActions.destroyChart),
        switchMap((action) =>
          this.store.select(fromChartSelectors.createChartResourceSelector(action.payload)).pipe(first())
        )
      )
      .subscribe((chart) => {
        this.reportSaveService.removeChart(chart.token);
        const queryToken = this.viewService.views.selectedQuery.token;

        if (this.viewService.views.viz.token === chart.token) {
          this.viewService.clearSelected();
        }

        // Show the current query if the deleted chart is the currently selected one
        if (this.viewService.views.queryMode && this.viewService.views.viz.token === chart.token) {
          this.$rootScope.$broadcast('report.selectQuery', {
            query: queryToken,
          });
        }

        chart
          .forms('destroy')
          .$submit()
          .then(() => {
            this.store.dispatch(ChartActions.destroyChartSuccess({ payload: chart.token }));
            this.reportDataService.updateReportSilently();
          })
          .catch((err) => {
            this.store.dispatch(
              ChartActions.destroyChartFailure({
                token: chart.token,
                queryToken: queryToken,
                error: err,
              })
            );
          });
      });
  }

  private saveTableSettings(table: TableState) {
    return table.resource
      .forms('edit')
      .$submit({
        'query_table.view': JSON.stringify(table.currentView),
      })
      .then((res) => {
        this.viewService.emitPreviewUpdate('Table', table.token, table.queryToken, res.view);
        return res;
      });
  }

  // TODO: move to table-data-service once that's repurposed
  public loadEmbeddedTables(queryToken: string, tables: TableResource[]) {
    this.store.dispatch({
      type: ChartModelActionType.LoadEmbeddedTablesSuccess,
      payload: tables,
      queryToken: queryToken,
    });
  }

  // Don't know what uses these functions
  public hasContent(queryToken) {
    const data = this.ds.getDatasetByKey(queryToken);

    return data && data.content && data.content.length;
  }

  public dataOversized(queryToken) {
    const data = this.ds.getDatasetByKey(queryToken);

    if (typeof data.oversized === 'boolean') {
      return data.oversized;
    } else {
      return null;
    }
  }

  public queryData(queryToken) {
    return this.ds.getDatasetByKey(queryToken);
  }

  public datasetState(queryToken) {
    return this.ds.getDatasetState(queryToken);
  }
}

export default ChartDataService;
